// Bridges Märka's consent decision into Shopify's Customer Privacy API, so the
// pixels that run inside Shopify checkout honour the choice the buyer already
// made on the storefront instead of asking again.
//
// WHY THIS EXISTS: Shopify gates everything that runs inside checkout — channel
// app pixels (Meta, Google, TikTok) and custom pixels alike — behind its *own*
// consent state, and blocks them for EU visitors until told otherwise. Our
// decision lives in localStorage (`consent-storage.ts`), which is origin-scoped:
// the checkout host cannot read what the storefront host wrote. Without this
// bridge the only alternatives are "no measurement from Swedish traffic" or
// enabling Shopify's own cookie banner, which asks the buyer the same question
// a second time at checkout.
//
// This is Shopify's documented headless path, not a workaround. Passing
// `headlessStorefront: true` together with both root domains makes the API write
// its `_tracking_consent` cookie against the SHARED root domain, which is
// exactly why checkout had to move onto a subdomain of markalappar.se. Shopify's
// docs are explicit that checkout must sit within the same root domain as the
// storefront for consent enforcement to work at all.
//
// localStorage remains the source of truth. This is a one-way projection of it
// and deliberately fire-and-forget: the call is a network round-trip to the
// Storefront API on the checkout domain, and a failure must cost pixel coverage
// inside checkout, never the user's actual choice.

import type { ConsentState } from "@/features/consent/consent-storage";

// Pinned version. Shopify has shipped breaking changes across these paths (the
// v0.1 → v0.2 bump changed the argument shape), so an unpinned URL would let
// behaviour change underneath us. Re-check the argument contract when bumping.
const CONSENT_API_SRC =
  "https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.2/consent-tracking-api.js";

interface ShopifyCustomerPrivacy {
  setTrackingConsent: (
    consent: Record<string, unknown>,
    callback: (result?: { error?: string }) => void,
  ) => void;
}

let scriptPromise: Promise<void> | null = null;

// Delivery is deduplicated at module scope rather than per caller, because
// `useConsent` is mounted several times on every page (ConsentBanner, MetaPixel,
// TikTokPixel, GoogleAdsTag) and each instance runs its own replay effect. Left
// alone that is one Storefront API round-trip per mount, per page load, all
// carrying identical values.
//
// `inFlightSignature` coalesces the concurrent case — the four effects fire in
// the same tick, so they must share one request rather than race four.
// `deliveredSignature` covers the sequential case across later navigations.
// Only a *successful* delivery is recorded, so a failure is retried by the next
// caller instead of being silently swallowed for the session.
let deliveredSignature: string | null = null;
let inFlightSignature: string | null = null;
let inFlight: Promise<boolean> | null = null;

function consentSignature(state: ConsentState): string {
  return `${state.analytics}:${state.marketing}`;
}

/** Test seam: drops the memoised loader and dedupe state for a clean slate. */
export function __resetShopifyConsentApiLoaderForTests(): void {
  scriptPromise = null;
  deliveredSignature = null;
  inFlightSignature = null;
  inFlight = null;
}

function loadConsentApi(): Promise<void> {
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${CONSENT_API_SRC}"]`,
    );
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = CONSENT_API_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Shopify consent-tracking-api failed to load"));
    document.head.appendChild(script);
  });

  // A rejected load must not poison every later attempt — the next call gets a
  // fresh chance (the visitor may simply have been offline, or a blocker may
  // have been switched off since).
  scriptPromise.catch(() => {
    scriptPromise = null;
  });

  return scriptPromise;
}

function getCustomerPrivacy(): ShopifyCustomerPrivacy | null {
  const shopify = (window as unknown as { Shopify?: { customerPrivacy?: unknown } }).Shopify;
  const api = shopify?.customerPrivacy;
  // The `Shopify` global appears before the API finishes wiring itself up, so
  // probe for the method rather than trusting the object's presence.
  return api && typeof (api as ShopifyCustomerPrivacy).setTrackingConsent === "function"
    ? (api as ShopifyCustomerPrivacy)
    : null;
}

interface ConsentDelivery {
  checkoutRootDomain: string;
  storefrontRootDomain: string;
  storefrontAccessToken: string;
}

async function deliverConsent(
  state: ConsentState,
  { checkoutRootDomain, storefrontRootDomain, storefrontAccessToken }: ConsentDelivery,
): Promise<boolean> {
  try {
    await loadConsentApi();
  } catch {
    return false;
  }

  const api = getCustomerPrivacy();
  if (!api) {
    return false;
  }

  return new Promise<boolean>((resolve) => {
    try {
      api.setTrackingConsent(
        {
          headlessStorefront: true,
          checkoutRootDomain,
          storefrontRootDomain,
          storefrontAccessToken,
          analytics: state.analytics === "granted",
          marketing: state.marketing === "granted",
          // Both hardcoded false, deliberately. Märka's banner offers exactly
          // two categories; `preferences` (remembering locale/currency inside
          // checkout) and `sale_of_data` (a CCPA concept with no Swedish
          // equivalent) were never asked about, and consent cannot be inferred
          // for a purpose the user was not shown. The only cost is that
          // checkout won't remember display preferences between visits, which
          // is invisible on a Swedish store selling to Swedes in SEK. Do not
          // flip these to `true` without adding the matching banner category.
          preferences: false,
          sale_of_data: false,
        },
        (result) => {
          resolve(!result?.error);
        },
      );
    } catch {
      resolve(false);
    }
  });
}

/**
 * Pushes the current decision to Shopify.
 *
 * Safe to call from every `useConsent` consumer on every render: identical
 * decisions are deduplicated (see `deliveredSignature`), so the several mounted
 * copies of the replay effect cost one request between them, not one each. A
 * genuine change — including a revoke — always goes through, which is what stops
 * a prior grant staying in force inside checkout.
 *
 * Resolves `false` when the decision could not be delivered — unconfigured
 * environment, blocked script, API never appeared, or Shopify returned an error.
 * Callers must not surface that: it is a measurement gap, not a user-facing
 * failure.
 */
export async function applyShopifyConsent(state: ConsentState): Promise<boolean> {
  if (typeof window === "undefined") {
    return false;
  }

  const checkoutRootDomain = process.env.NEXT_PUBLIC_SHOPIFY_CHECKOUT_DOMAIN;
  const storefrontRootDomain = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ROOT_DOMAIN;
  const storefrontAccessToken = process.env.NEXT_PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN;

  // Absent in local dev and preview deploys. No-op rather than throw, matching
  // `conversions.ts` and `instrumentation-client.ts` — a missing env must never
  // break the consent banner itself.
  if (!checkoutRootDomain || !storefrontRootDomain || !storefrontAccessToken) {
    return false;
  }

  const signature = consentSignature(state);

  // Already landed this exact decision — nothing for Shopify to learn.
  if (signature === deliveredSignature) {
    return true;
  }

  // The mounted replay effects fire in the same tick; share one request.
  if (inFlight && signature === inFlightSignature) {
    return inFlight;
  }

  inFlightSignature = signature;
  inFlight = deliverConsent(state, {
    checkoutRootDomain,
    storefrontRootDomain,
    storefrontAccessToken,
  })
    .then((delivered) => {
      // Only a success is recorded, so a failure is retried by the next caller
      // rather than being swallowed for the rest of the session.
      if (delivered) {
        deliveredSignature = signature;
      }
      return delivered;
    })
    .finally(() => {
      // Guarded: a newer decision may already have replaced this one in flight.
      if (inFlightSignature === signature) {
        inFlight = null;
        inFlightSignature = null;
      }
    });

  return inFlight;
}
